import { fetchProducts, loadMoreProducts } from '@/redux/api/storeApiSlice'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { captureEventPosthog } from '@/services/Posthog'
import {
    PbshoppingMenuItem,
    StoreApiFetchProductsRequest
} from '@itsrever/rever-api'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

export function useStoreProducts(
    menu: PbshoppingMenuItem[],
    selectedCategoryIndex: number,
    filterText: string,
    isMenuLoaded: boolean
) {
    const dispatch = useAppDispatch()
    const { i18n } = useTranslation()
    const settings = useAppSelector(
        (store) => store.settingsApi.settings.response
    )
    const exchangeFlow = useAppSelector(
        (store) => store.settingsApi.settings.response.exchange_flow
    )

    const isInitialLoading = useRef(true)

    const productsCall = useAppSelector((store) => store.store.products)
    const products = productsCall.products.products ?? []
    const pageInfo = productsCall.pageInfo
    const loadingProducts = productsCall.loading
    const countryCode =
        useAppSelector((store) => store.return.returnAddress.country_code) ??
        'ES'
    const orderCurrency =
        useAppSelector(
            (store) => store.returnsApi.order.response?.order?.money_format
        )?.currency ?? 'unknown'
    const language = i18n.language.toLocaleUpperCase() ?? 'EN'
    const ecommerceId = settings.ecommerce_id ?? ''

    const resourceId = menu[selectedCategoryIndex]?.resource_id ?? ''
    const resourceType = menu[selectedCategoryIndex]?.resource_type ?? ''

    const loadNextPage = () => {
        if (pageInfo.has_next_page === false) return
        let fetchProductsParams: StoreApiFetchProductsRequest = {
            ecommerceId,
            resourceId,
            resourceType,
            language,
            countryCode,
            orderCurrency,
            pageInfoPageSize: 20
        }
        if (filterText !== '') {
            fetchProductsParams = {
                ...fetchProductsParams,
                resourceId: '',
                resourceType: '',
                filterText
            }
        }
        dispatch(
            loadMoreProducts({
                ...fetchProductsParams,
                pageInfoStartCursor: pageInfo.end_cursor ?? ''
            })
        )
    }

    useEffect(() => {
        if (!isMenuLoaded) return
        if (exchangeFlow !== 'FULL') return
        const fetchStoreProducts = async () => {
            let fetchProductsParams: StoreApiFetchProductsRequest = {
                ecommerceId,
                resourceId,
                resourceType,
                language,
                countryCode,
                orderCurrency,
                pageInfoPageSize: 20
            }
            if (filterText !== '') {
                fetchProductsParams = {
                    ...fetchProductsParams,
                    resourceId: '',
                    resourceType: '',
                    filterText
                }
            }
            dispatch(fetchProducts(fetchProductsParams))

            if (isInitialLoading.current) {
                isInitialLoading.current = false
            }
        }

        fetchStoreProducts()
    }, [
        selectedCategoryIndex,
        filterText,
        isMenuLoaded,
        ecommerceId,
        resourceId,
        resourceType,
        language,
        countryCode,
        orderCurrency,
        exchangeFlow,
        dispatch
    ])

    useEffect(() => {
        if (productsCall.loading === 'succeeded') {
            captureEventPosthog('FC - Products loaded', {
                ecommerceId,
                productIDs: products.map((product) => product.id),
                productCount: products.length,
                filterText,
                resourceId,
                resourceType,
                categoriesOffered: menu.map((category) => category.resource_id),
                categoriesCount: menu.length
            })
        }
    }, [productsCall.loading])

    return {
        products,
        pageInfo,
        loadNextPage,
        isLoadingProducts:
            loadingProducts === 'pending' || isInitialLoading.current
    }
}
